import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomeButton from "~/components/Button";
import PropTypes from "prop-types";
import CommentIcon from "../../../assets/comment.svg";
import TaskCommentSuccessView from "./TaskCommentSuccessView";
import { useNavigate, useParams } from "react-router-dom";
import TaskCommentError from "./TaskCommentError";
import TaskAttachmentList from "./TaskAttachmentList";
import { useRef } from "react";
import { FileUpload } from "primereact/fileupload";
import Categories from "~/components/Processes/conversations/utils/categories.json";
import { fetchServices } from "~/components/global-entities/globalEntitiesSlice";
import { getName } from "country-list";
import useFileUpload from "~/utils/useFileUpload";
import { mergeCategories } from "~/Pages/Requests/components/new-request-modal/utils";

const TaskCommentModal = ({
  visible,
  onCancel,
  task,
  onChange,
  taskCommentData,
  onConfirm,
  isLoading,
  showSuccess,
  isError,
  setIsError,
  setFiles,
  files,
  errorMessage,
  setErrorMessage,
  setIsTaskCommentModal,
  setShowAttachmentConfirmation,
}) => {
  const { id } = useParams();
  const { role } = useSelector((state) => state.login);
  const process = useSelector((state) => state.process?.processdata);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // Helper function to replace LSP with Local Team in display text
  const formatDisplayText = (text: string) => {
    if (!text) return text;
    return text.replace(/\bLSP\b/g, "Local Team");
  };
  const [commentType, setCommentType] = useState<{
    name: string;
    code: string;
  } | null>(null);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [validFile, setValidFile] = useState(false);
  const { entityservices } = useSelector((state) => state.globalEntities);
  const { uuidToUlid, SERVICE_CODES, getFileVersion, getFormattedProcessName } =
    useFileUpload();
  const treeData = mergeCategories([process?.serviceData?.name], role)?.map(
    (category) => ({
      key: category?.code,
      label: category?.name,
      selectable: false,
      children: category?.subCategory?.map((sub) => ({
        key: sub?.code,
        label: sub?.name,
        selectable: true,
      })),
    }),
  );
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchServices());
  }, []);

  useEffect(() => {
    if (files?.length) {
      const isValid = files.some((file) => file.code === null);
      setValidFile(isValid);
    } else {
      setValidFile(false);
    }
  }, [files]);
  const COMMENT_OPTIONS = [
    { name: "Internal (HSP & Local Team Only)", code: "internal" },
    { name: "Public (Include customer)", code: "public" },
  ];
  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];

  const disableBtnHandler = () => {
    const hasComment = !!taskCommentData?.comment?.trim();
    const hasFiles = files?.length > 0;

    if (role !== "CUSTOMER" && role !== "CUSTOMER_MANAGER") {
      const hasType = !!taskCommentData?.type;
      setIsDisabled(!(hasType && (hasComment || hasFiles)));
    } else {
      setIsDisabled(!(hasComment || hasFiles));
    }
  };
  useEffect(() => {
    const selectedType = COMMENT_OPTIONS.find(
      (opt) => opt.code === taskCommentData?.type,
    );
    if (selectedType) setCommentType(selectedType);
    disableBtnHandler();
  }, [taskCommentData, role, files]);

  const getSuccessViewHeader = () => {
    const type =
      taskCommentData?.type && taskCommentData?.type === "internal"
        ? "Internal"
        : "Public";
    return `${type} Comment Added to ${task.name}`;
  };
  const getSuccessViewDescription = () => {
    const isInternal = taskCommentData?.type === "internal";
    const conversationType = isInternal ? "HSP" : "Project";
    const typeOfComment = isInternal ? "internal" : "public";
    return `Your comment has been marked as ${typeOfComment} and will be added to the ${conversationType} conversation.`;
  };
  const navigationHandler = () => {
    const tab =
      taskCommentData?.type && taskCommentData?.type === "internal"
        ? "conversations/internal"
        : "conversations/public";
    navigate(`/process/${id}/${tab}`);
  };

  const itemTemplate = (option) => {
    return (
      <div className="flex align-items-center comment-type-template">
        <input
          type="radio"
          checked={commentType?.name === option.name}
          readOnly
          className="type-radio"
        />
        <span>{option.name}</span>
      </div>
    );
  };

  useEffect(() => {
    if (isError) {
      const timer = setTimeout(() => {
        setIsError("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [isError, setIsError]);

  const getFileSizeMB = (file) => (file.size / (1024 * 1024)).toFixed(2);

  const onFileUpload = (e) => {
    const validFiles = e.files
      .map((file) => {
        const extension = file.name
          ? file.name.split(".").pop().toLowerCase()
          : "";
        const size = Number(getFileSizeMB(file));

        if (!fileSupported.includes(extension)) {
          setIsError(true);
          setErrorMessage({
            summary: "Unsupported file type. ",
            detail: "Please select a file with a supported format.",
          });
          return null;
        }
        if (size > 20) {
          setIsError(true);
          setErrorMessage({
            summary: "File size exceeds the maximum allowed limit. ",
            detail: "Please select a smaller file.",
          });
          return null;
        }

        return {
          file: Object.assign(file, { fileName: file.name }),
          code: null,
        };
      })
      .filter(Boolean);

    setFiles((prevFiles) => {
      const newFiles = validFiles.filter(
        (newFile) =>
          !prevFiles.some(
            (existingFile) =>
              existingFile.file.name === newFile.file.name &&
              existingFile.file.size === newFile.file.size,
          ),
      );
      return [...prevFiles, ...newFiles];
    });

    e.files = [];
    if (fileInputRef.current) {
      fileInputRef.current.clear();
    }
  };
  const onFileRemove = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };

  const handleSelectCategory = async (e, index) => {
    let parentCategory = null;
    let childCategory = null;

    treeData.forEach((cat) => {
      cat.children.forEach((sub) => {
        if (sub.key === e.value) {
          parentCategory = cat;
          childCategory = sub;
        }
      });
    });

    if (!parentCategory) {
      parentCategory = treeData.find((cat) => cat.key === e.value);
    }

    const fileType = files[index]?.file?.name.split(".").pop();
    const entityName = process?.entityData?.name;
    const country = getName(process?.entityData?.country);
    const serviceLine = entityservices.find(
      (x) => x.id === process?.service,
    )?.name;
    const processName = process?.name;
    const category = parentCategory?.label;
    const categoryCode = parentCategory?.key;
    const subCategory = childCategory?.label;
    const subCategoryCode = childCategory?.key;

    const tags = {
      category: categoryCode,
      subCategory: subCategoryCode,
      entity: uuidToUlid(process?.entity),
      service: SERVICE_CODES[process?.serviceData?.name],
      is_public: taskCommentData?.type === "internal" ? "false" : "true",
      country: process?.entityData?.country,
      process: uuidToUlid(process?.id),
      year: new Date().getFullYear().toString(),
      temporary_file: "false",
    };
    let version = `${await getFileVersion(tags)}`;
    if (files?.length > 0) {
      const matchingFiles = files.filter(
        (item) =>
          item.file.category === categoryCode &&
          item.file.subCategory === subCategoryCode,
      );
      if (matchingFiles.length > 0) {
        version =
          Math.max(
            ...matchingFiles.map((item) => Number(item.file.versionNumber)),
          ) + 1;
      }
    }
    const newFileName = `${entityName}_${country}_${serviceLine}_${getFormattedProcessName(process)}_${category}_${subCategory}_${version}.${fileType}`;

    const updatedFiles = [...files];
    updatedFiles[index]["file"]["fileName"] = newFileName;
    updatedFiles[index]["code"] = childCategory?.key;
    updatedFiles[index]["file"]["category"] = categoryCode;
    updatedFiles[index]["file"]["subCategory"] = subCategoryCode;
    updatedFiles[index]["file"]["versionNumber"] = Number(version);

    setFiles(updatedFiles);
  };

  return (
    <div>
      <Dialog
        visible={visible}
        onHide={onCancel}
        className="task-comment-modal"
      >
        {!showSuccess ? (
          <div className="task-comment-modal-content">
            <div className="header flex justify-content-between align-items-center">
              <p className="task-comment-modal-heading">
                Add Comment to "{formatDisplayText(task.name)}"
              </p>

              <i
                className="pi pi-times cross-icon mr-0"
                onClick={onCancel}
                style={{ cursor: "pointer" }}
              ></i>
            </div>
            {/* error */}
            {isError && (
              <TaskCommentError
                summary={errorMessage.summary}
                detail={errorMessage.detail}
              />
            )}

            {role !== "CUSTOMER" && role !== "CUSTOMER_MANAGER" && (
              <div className="comment-type-container flex flex-column gap-1">
                <div className="flex">
                  <p className="comment-type-label m-0">Comment Type*</p>
                </div>
                <Dropdown
                  placeholder="Select type"
                  value={commentType}
                  options={COMMENT_OPTIONS}
                  onChange={(e) => onChange(e, "type")}
                  optionLabel="name"
                  itemTemplate={itemTemplate}
                />
              </div>
            )}

            <InputTextarea
              id="task_comment"
              name="task_comment"
              value={taskCommentData?.comment}
              onChange={(e) => onChange(e, "comment")}
              className={`input-field appearance-none outline-none w-full`}
              rows={18}
              maxLength={4000}
              placeholder="Add your message here..."
            />

            <TaskAttachmentList
              files={files}
              onFileRemove={onFileRemove}
              isLoading={isLoading}
              handleSelectCategory={handleSelectCategory}
              treeData={treeData}
            />
            <div className="flex justify-content-end gap-2 mt-2">
              <FileUpload
                ref={fileInputRef}
                name="files"
                customUpload={true}
                onSelect={onFileUpload}
                chooseOptions={{
                  icon: "pi pi-paperclip",
                  label: "Add Attachments",
                  iconOnly: false,
                  className: "select-file-btn",
                }}
                mode="advanced"
              />
              <CustomeButton
                label="Send"
                variant="contained"
                onClick={() => {
                  if (files.length > 0) {
                    setShowAttachmentConfirmation(true);
                    setIsTaskCommentModal(false);
                  } else {
                    onConfirm(task.id);
                  }
                }}
                loading={isLoading}
                disabled={isDisabled || isLoading || validFile}
                className={`add-comment-btn ${(isDisabled || validFile) && "disable"}`}
              />
            </div>
          </div>
        ) : (
          <TaskCommentSuccessView
            Icon={<img src={CommentIcon} alt="Comment Icon" />}
            Header={getSuccessViewHeader()}
            Description={getSuccessViewDescription()}
            onCancel={onCancel}
            onNavigate={navigationHandler}
          />
        )}
      </Dialog>
    </div>
  );
};

TaskCommentModal.propTypes = {
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  task: PropTypes.object,
  onChange: PropTypes.func,
  data: PropTypes.object,
  taskCommentData: PropTypes.object,
  onConfirm: PropTypes.func,
  isLoading: PropTypes.bool,
  showSuccess: PropTypes.bool,
  isError: PropTypes.bool,
  setIsError: PropTypes.func,
  setIsTaskCommentModal: PropTypes.func,
  showAttachmentConfirmation: PropTypes.bool,
  setShowAttachmentConfirmation: PropTypes.func,
};

export default TaskCommentModal;
